import { useState } from "react";
import { Link2, Check } from "lucide-react";
import { useGetMyInvite } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

// Quiet, one-line sharing note. Never an alert component.
export const INVITE_SHARING_NOTE =
  "Share it directly or in alumni spaces, and please keep it off public posts.";

// Dashboard variant combines the name-disclosure passage with the sharing note
// as a single muted line.
export const INVITE_DASHBOARD_NOTE =
  "This link carries your name: anyone who sees or opens it will know you invited them. " +
  INVITE_SHARING_NOTE;

interface CopyInviteButtonProps {
  className?: string;
  // When false, renders a neutral (bordered) button instead of the crimson fill.
  filled?: boolean;
}

// Shared "Copy my invite link" action. Fetches the member's personal invite lazily
// and copies the full invite URL to the clipboard.
export function CopyInviteButton({ className, filled = true }: CopyInviteButtonProps) {
  const { data: invite, isLoading } = useGetMyInvite();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!invite?.inviteUrl) return;
    try {
      await navigator.clipboard.writeText(invite.inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Invite link copied",
        description: "Your personal invite link is on your clipboard.",
      });
    } catch {
      toast({
        title: "Could not copy",
        description: "Copy the link manually from your dashboard.",
        variant: "destructive",
      });
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={isLoading || !invite?.inviteUrl}
      className={cn(
        "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60",
        filled
          ? "bg-primary text-primary-foreground hover:bg-primary/90"
          : "border border-border text-foreground hover:bg-muted",
        className,
      )}
    >
      {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
      {copied ? "Copied" : "Copy my invite link"}
    </button>
  );
}
